/**
 * Helper functions for FileMasterConfigV2 components.
 */

/**
 * Generate a snake_case key from a display name.
 * @param {string} name - e.g. "SBI Bank Statement" or "sbi_statement.csv"
 * @returns {string} - e.g. "sbi_bank_statement"
 */
export function generateKey(name) {
  if (!name) return '';
  return name
    .replace(/\.[^/.]+$/, '') // strip file extension
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Ensure key uniqueness within an array of existing keys.
 * Appends _2, _3, etc. on collision.
 * @param {string} key
 * @param {string[]} existingKeys
 * @returns {string}
 */
export function uniqueKey(key, existingKeys) {
  if (!existingKeys.includes(key)) return key;
  let counter = 2;
  while (existingKeys.includes(`${key}_${counter}`)) {
    counter++;
  }
  return `${key}_${counter}`;
}


/**
 * Infer data type from a column name string.
 * @param {string} columnName
 * @returns {'string'|'number'|'date'|'time'}
 */
export function inferDataType(columnName) {
  if (!columnName) return 'string';
  const lower = columnName.toLowerCase();
  if (/amount|net\b|debit|credit|balance|total|sum|price|cost|value|qty|quantity|rate/.test(lower)) return 'number';
  if (/\btime\b|timestamp/.test(lower)) return 'time';
  if (/date|dt\b/.test(lower)) return 'date';
  return 'string';
}

/**
 * Attempt case-insensitive auto-match of a base column name against a file's headers.
 * @param {string} baseColumn
 * @param {string[]} headers
 * @returns {string|null} - matched header or null
 */
export function autoMatchHeader(baseColumn, headers) {
  if (!baseColumn || !headers || !headers.length) return null;
  const lowerBase = baseColumn.toLowerCase().trim();
  // Exact case-insensitive match
  const match = headers.find((h) => h.toLowerCase().trim() === lowerBase);
  return match || null;
}

/**
 * Generate a title-cased display name from a file name.
 * @param {string} fileName - e.g. "sbi_bank_statement.csv"
 * @returns {string} - e.g. "Sbi Bank Statement"
 */
export function fileNameToDisplayName(fileName) {
  if (!fileName) return '';
  const base = fileName.replace(/\.[^/.]+$/, ''); // strip extension
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Build the final v2 file config payload from step data.
 * @param {Object} stepOneData - from Step 1
 * @param {Object} stepTwoData - from Step 2 V2
 * @param {string} selectFileName - selected file master id
 * @returns {Object}
 */
export function buildV2Payload(stepOneData, stepTwoData, selectFileName) {
  return {
    version: 'v2',
    file: selectFileName,
    ignorerows: stepOneData.ignoreRow,
    ignorecolumns: stepOneData.ignoreColums,
    ignorefooterrows: stepOneData.ignoreFooterRows,
    blankcolumnheaders: stepOneData.blankheaders,
    removeblankrows: stepOneData.blankRows,
    keepduplicatecolumns: stepOneData.duplicateColumns,
    fileTypes: stepTwoData.fileTypes,
    columnmap: stepTwoData.columnmap,
    primaryKeys: stepTwoData.primaryKeys,
  };
}
